import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  SafetyOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal, Spin } from "antd";
import ChangePasswordViewModel from "features/changePassword/viewModel/ChangePasswordViewModel";
import { FC } from "react";
import { strings } from "utils/localizedStrings";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const { error, handleOnChange, loading, onSubmit, isSuccess } =
    ChangePasswordViewModel();

  const handleSubmit = async () => {
    await onSubmit();
    if (isSuccess) {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={760}
      styles={{ header: { textAlign: "center" } }}
      closeIcon={
        <div className="w-[32px] h-[32px] rounded-full border-2 border-[#9CA3AF] flex items-center justify-center">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
              stroke="#4B5563"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
              stroke="#4B5563"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      }
      title={
        <p className="text-[22px] font-[700] leading-none">
          {strings.ChangePassword.Title}
        </p>
      }
    >
      <Spin spinning={loading}>
        <div className="md:w-4/5 mb-2 mx-auto">
          <Form>
            <div>
              <div>{strings.ChangePassword.OldPass}</div>
              <Form.Item
                name="oldPassword"
                className="h-18 pb-1"
                validateStatus={error.oldPassword ? "error" : undefined}
                help={
                  error.oldPassword
                    ? strings.ChangePassword.Message.OldPass
                    : undefined
                }
              >
                <Input.Password
                  size="large"
                  name="oldPassword"
                  className="rounded-[10px] h-12"
                  placeholder={strings.ChangePassword.Message.OldPass}
                  prefix={<SafetyOutlined className="pr-1" />}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  onChange={handleOnChange}
                />
              </Form.Item>
            </div>
            <div>
              <div>{strings.ChangePassword.NewPass}</div>
              <Form.Item
                name="password"
                className="h-18 pb-1"
                validateStatus={error.password ? "error" : undefined}
                help={
                  error.password ? strings.ChangePassword.NewPass : undefined
                }
              >
                <Input.Password
                  size="large"
                  name="password"
                  className="rounded-[10px] h-12"
                  placeholder={strings.ChangePassword.Message.NewPass}
                  prefix={<UnlockOutlined className="pr-1" />}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  onChange={handleOnChange}
                />
              </Form.Item>
            </div>
            <div>
              <div>{strings.ChangePassword.Reconfirmed}</div>
              <Form.Item
                name="confirmPassword"
                className="h-18 pb-1"
                validateStatus={error.confirmPassword ? "error" : undefined}
                help={
                  error.password
                    ? strings.ChangePassword.Reconfirmed
                    : error?.confirmPassword
                    ? strings.ChangePassword.Message.UnmatchedPass
                    : undefined
                }
              >
                <Input.Password
                  size="large"
                  name="confirmPassword"
                  className="rounded-[10px] h-12"
                  placeholder={strings.ChangePassword.Message.NewPass}
                  prefix={<LockOutlined className="pr-1" />}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  onChange={handleOnChange}
                />
              </Form.Item>
            </div>
            <div className="mt-6">
              <Button
                className="rounded-[10px] h-14 w-full text-[16px]"
                type="primary"
                htmlType="submit"
                onClick={handleSubmit}
              >
                {strings.GlobalLabels.Confirmed}
              </Button>
            </div>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
};

export default ChangePasswordModal;
